import { Field } from 'formik';
import {
  Box,
  Grid,
  GridItem,
  Heading,
  Text,
  VStack,
  HStack,
  Input,
} from '@chakra-ui/react';
import { VTOTextField } from './VTOTextField';
import { VTOArrayField } from './VTOArrayField';
import { NumericTargetInput } from './NumericTargetInput';
import { TargetMarketField } from './TargetMarketField';
import { VTODocument } from '../../types';

interface VisionViewProps {
  isEditing: boolean;
  isPrinting?: boolean;
}

/**
 * Vision View Component
 * Displays and allows editing of the Vision section of the V/TO
 * Migrated from Tailwind to Chakra UI, uses Formik Field components
 */
export function VisionView({ isEditing, isPrinting = false }: VisionViewProps) {
  return (
    <Box maxW="5xl" mx="auto">
      <Box borderWidth="2px" borderColor="gray.900" minH="800px">
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          borderBottomWidth="2px"
          borderColor="gray.900"
          bg="gray.100"
          py={3}
        >
          <Heading size="lg" color="gray.900" letterSpacing="wide">
            VISION
          </Heading>
        </Box>

        <Grid 
          templateColumns={isPrinting ? "repeat(2, 1fr)" : { base: "1fr", md: "repeat(2, 1fr)" }} 
          minH="760px"
        >
          <GridItem
            borderRightWidth={isPrinting ? "2px" : { md: "2px" }}
            borderColor="gray.900"
            p={{ base: 4, md: 6 }}
          >
            <VStack spacing={6} align="stretch">
              {/* Core Values */}
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  borderBottomWidth="2px"
                  borderColor="blue.500"
                  pb={isPrinting ? 3 : 1}
                  mb={isPrinting ? 4 : 3}
                >
                  <Heading size="xs" color="blue.700" textTransform="uppercase" letterSpacing="wider">
                    Core Values
                  </Heading>
                </Box>
                <Field
                  name="coreValues"
                  component={VTOArrayField}
                  placeholder="Enter core value"
                  addButtonText="Add Value"
                  isEditing={isEditing}
                  showNumbers={true}
                  emptyStateText="Click Edit to add Core Values"
                />
              </Box>

              {/* Core Focus */}
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  borderBottomWidth="2px"
                  borderColor="purple.500"
                  pb={isPrinting ? 3 : 1}
                  mb={isPrinting ? 4 : 3}
                >
                  <Heading size="xs" color="purple.700" textTransform="uppercase" letterSpacing="wider">
                    Core Focus
                  </Heading>
                </Box>
                <VStack spacing={3} align="stretch">
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                      Purpose/Cause/Passion
                    </Text>
                    <Field
                      name="purpose"
                      component={VTOTextField}
                      placeholder="What is your company's purpose, cause, or passion?"
                      rows={3}
                      isEditing={isEditing}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                      Niche
                    </Text>
                    <Field
                      name="niche"
                      component={VTOTextField}
                      placeholder="What is your niche? Who do you serve and how?"
                      rows={3}
                      isEditing={isEditing}
                    />
                  </Box>
                </VStack>
              </Box>

              {/* 10-Year Target */}
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  borderBottomWidth="2px"
                  borderColor="emerald.500"
                  pb={isPrinting ? 3 : 1}
                  mb={isPrinting ? 4 : 3}
                >
                  <Heading size="xs" color="emerald.700" textTransform="uppercase" letterSpacing="wider">
                    10-Year Target
                  </Heading>
                </Box>
                <Field
                  name="tenYearTarget"
                  component={VTOTextField}
                  placeholder="Where do you see the company in 10 years?"
                  rows={2}
                  isEditing={isEditing}
                />
              </Box>

              {/* Marketing Strategy */}
              <Box>
                <Box
                  display="flex"
                  alignItems="center"
                  borderBottomWidth="2px"
                  borderColor="orange.500"
                  pb={isPrinting ? 3 : 1}
                  mb={isPrinting ? 4 : 3}
                >
                  <Heading size="xs" color="orange.700" textTransform="uppercase" letterSpacing="wider">
                    Marketing Strategy
                  </Heading>
                </Box>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                      Target Market / "The List"
                    </Text>
                    <TargetMarketField isEditing={isEditing} />
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                      Three Uniques
                    </Text>
                    {!isEditing ? (
                      <VStack spacing={2} align="stretch">
                        {[0, 1, 2].map((idx) => (
                          <HStack key={idx} spacing={2}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700">
                              {idx + 1}.
                            </Text>
                            <Field name={`threeUniques.${idx}`}>
                              {({ field }: any) => (
                                <Text fontSize="sm" color="gray.700" flex={1}>
                                  {field.value || <Text as="span" color="gray.400" fontStyle="italic">Not defined</Text>}
                                </Text>
                              )}
                            </Field>
                          </HStack>
                        ))}
                      </VStack>
                    ) : (
                      <VStack spacing={2} align="stretch">
                        {[0, 1, 2].map((idx) => (
                          <HStack key={idx} spacing={2}>
                            <Text fontSize="sm" fontWeight="semibold" color="gray.700" minW="1.5rem">
                              {idx + 1}.
                            </Text>
                            <Field name={`threeUniques.${idx}`}>
                              {({ field }: any) => (
                                <Input
                                  {...field}
                                  placeholder="What makes you unique?"
                                  size="sm"
                                  flex={1}
                                />
                              )}
                            </Field>
                          </HStack>
                        ))}
                      </VStack>
                    )}
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                      Proven Process
                    </Text>
                    <Field
                      name="provenProcess"
                      component={VTOTextField}
                      placeholder="What is your proven, repeatable process?"
                      rows={2}
                      isEditing={isEditing}
                    />
                  </Box>
                  <Box>
                    <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={1}>
                      Guarantee
                    </Text>
                    <Field
                      name="guarantee"
                      component={VTOTextField}
                      placeholder="What guarantee do you offer?"
                      rows={2}
                      isEditing={isEditing}
                    />
                  </Box>
                </VStack>
              </Box>
            </VStack>
          </GridItem>

          <GridItem p={6}>
            <Box>
              <Box
                display="flex"
                alignItems="center"
                borderBottomWidth="2px"
                borderColor="teal.500"
                pb={isPrinting ? 3 : 1}
                mb={isPrinting ? 4 : 3}
              >
                <Heading size="xs" color="teal.700" textTransform="uppercase" letterSpacing="wider">
                  3-Year Picture
                </Heading>
              </Box>
              <VStack spacing={3} align="stretch">
                <HStack spacing={2} align="center">
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                    Future Date:
                  </Text>
                  <Box flex={1}>
                    <Field
                      name="threeYearDate"
                      component={VTOTextField}
                      placeholder="e.g., December 31, 2027"
                      rows={1}
                      isEditing={isEditing}
                      notDefinedText="Not set"
                    />
                  </Box>
                </HStack>
                <HStack spacing={2} align="center">
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                    Revenue:
                  </Text>
                  <Box flex={1}>
                    <Field
                      name="threeYearRevenueTarget"
                      component={NumericTargetInput}
                      placeholder="e.g., 50M"
                      isEditing={isEditing}
                      notDefinedText="Not set"
                    />
                  </Box>
                </HStack>
                <HStack spacing={2} align="center">
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" w={isPrinting ? 'auto' : 24} mr={isPrinting ? 4 : 0}>
                    Profit:
                  </Text>
                  <Box flex={1}>
                    <Field
                      name="threeYearProfitTarget"
                      component={NumericTargetInput}
                      placeholder="e.g., 8M"
                      isEditing={isEditing}
                      notDefinedText="Not set"
                    />
                  </Box>
                </HStack>
                {isEditing && (
                  <Box bg="orange.50" p={3} rounded="md">
                    <Text fontSize="xs" fontWeight="semibold" color="orange.700" mb={2}>
                      Reviews Targets
                    </Text>
                    <VStack spacing={2} align="stretch">
                      <HStack spacing={2} align="center">
                        <Text fontSize="xs" color="orange.700" flex={1}>
                          Forecasted Loans
                        </Text>
                        <Field name="threeYearForecastedLoans">
                          {({ field, form }: any) => (
                            <Input
                              type="number"
                              size="sm"
                              width="120px"
                              value={field.value ?? 0}
                              onChange={(e) =>
                                form.setFieldValue('threeYearForecastedLoans', e.target.value === '' ? 0 : Number(e.target.value))
                              }
                              borderColor="orange.300"
                            />
                          )}
                        </Field>
                      </HStack>
                      <HStack spacing={2} align="center">
                        <Text fontSize="xs" color="orange.700" flex={1}>
                          Review Target %
                        </Text>
                        <Field name="threeYearReviewTargetPct">
                          {({ field, form }: any) => (
                            <Input
                              type="number"
                              size="sm"
                              width="120px"
                              value={field.value ?? 0}
                              onChange={(e) =>
                                form.setFieldValue('threeYearReviewTargetPct', e.target.value === '' ? 0 : Number(e.target.value))
                              }
                              borderColor="orange.300"
                            />
                          )}
                        </Field>
                      </HStack>
                    </VStack>
                  </Box>
                )}
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                    Measurables
                  </Text>
                  <Field
                    name="threeYearMeasurables"
                    component={VTOArrayField}
                    placeholder="Key metric (e.g., 500 employees)"
                    addButtonText="Add Measurable"
                    isEditing={isEditing}
                  />
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="semibold" color="gray.600" mb={2}>
                    What Does It Look Like
                  </Text>
                  <Field
                    name="threeYearDescription"
                    component={VTOArrayField}
                    placeholder="Describe your company in 3 years"
                    addButtonText="Add Description"
                    isEditing={isEditing}
                  />
                </Box>
              </VStack>
            </Box>
          </GridItem>
        </Grid>
      </Box>
    </Box>
  );
}


